/**
 * Local type contract + pure helpers for the Category frontend.
 *
 * `Category` mirrors the wire shape of `GET /py-api/v1/categories`
 * (`schemas/category.serialize_category`) field for field. It is declared here
 * rather than imported because the backend is Python — there is no shared type
 * to import — so this file IS the contract, and it must be updated in step with
 * that serializer.
 *
 * THE TAXONOMY IS TWO LEVELS DEEP and no more: `parentId` is either null (a
 * top-level category) or the id of a top-level category. That mirrors the
 * BudgetBakers Wallet taxonomy AIM replaces — `Snacks` under `Food & Drinks`,
 * `Public transport` under `Transportation` — and is enforced server-side in
 * `services/category._validate_parent`. Nothing here may assume deeper nesting
 * is renderable, and nothing here may create it.
 */

export type CategoryKind = "INCOME" | "EXPENSE";

/** Shared between `CategoryManager.tsx`'s inline colour popover and
 *  `CreateCategorySheet.tsx`'s colour picker, so the two palettes can never
 *  drift apart. Distinguishable at both the 24px row swatch and the 36px
 *  sheet preview chip. */
export const PRESET_COLORS: readonly string[] = [
  "#ef4444",
  "#f59e0b",
  "#10b981",
  "#14b8a6",
  "#3b82f6",
  "#6366f1",
  "#a855f7",
  "#ec4899",
  "#6b7280",
];

export interface Category {
  id: string;
  name: string;
  kind: CategoryKind;
  parentId: string | null;
  color: string | null;
  /** Curated lucide icon name (e.g. "coffee"), or null. Migration 0033
   *  (Wave 5, agent A2) — see `./categoryIcons.ts` for the allowed set and the
   *  name -> component map. */
  icon?: string | null;
  sortOrder: number;
  isSystem: boolean;
  isArchived: boolean;
  /** Present only when the list was fetched with `?withUsage=true`. Records
   *  (Income + Expense) currently tagged with this category, scoped per
   *  `GET /categories`'s own-data-by-default rule. */
  usageCount?: number;
  /** This calendar month's total across those same records, in paise. */
  monthTotalPaise?: number;
}

export interface CategoryTreeNode extends Category {
  children: Category[];
}

export interface MergeMovedCounts {
  incomes: number;
  expenses: number;
  budgets: number;
}

const MAX_RECENT = 6;

/**
 * Normalizes the /api/categories response envelope, which may be a bare
 * array OR `{ categories: [...] }` — never throws on unexpected shapes.
 */
export function normalizeCategoriesResponse(json: unknown): Category[] {
  if (Array.isArray(json)) return json as Category[];
  if (
    json &&
    typeof json === "object" &&
    Array.isArray((json as { categories?: unknown }).categories)
  ) {
    return (json as { categories: Category[] }).categories;
  }
  return [];
}

/** localStorage key for a kind's recent-category id list. */
export function recentStorageKey(kind: CategoryKind): string {
  return `aim-recent-categories-${kind}`;
}

/** Reads the recent-id list for a kind. Safe to call with no DOM (SSR/tests). */
export function readRecentIds(kind: CategoryKind): string[] {
  if (typeof window === "undefined" || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(recentStorageKey(kind));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === "string").slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

/**
 * Records `id` as the most-recently-used category for `kind`, moving it to
 * the front and capping the list at 6. Returns the new list (also persisted).
 */
export function recordRecentId(kind: CategoryKind, id: string): string[] {
  const current = readRecentIds(kind);
  const next = [id, ...current.filter((x) => x !== id)].slice(0, MAX_RECENT);
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.setItem(recentStorageKey(kind), JSON.stringify(next));
    } catch {
      // storage full / disabled — recent-first is a nicety, never fatal
    }
  }
  return next;
}

/**
 * Reorders `categories` so that non-archived recent ids come first (in
 * recency order), followed by the remaining categories in their original
 * order. Archived categories and unknown ids are dropped from the recent set
 * but still appear in the remainder if present.
 */
export function orderRecentFirst(categories: Category[], recentIds: string[]): Category[] {
  const byId = new Map(categories.map((c) => [c.id, c] as const));
  const recent = recentIds
    .map((id) => byId.get(id))
    .filter((c): c is Category => !!c && !c.isArchived);
  const recentSet = new Set(recent.map((c) => c.id));
  const rest = categories.filter((c) => !recentSet.has(c.id));
  return [...recent, ...rest];
}

/**
 * Sibling order, at both levels: `sortOrder` then name.
 *
 * The same order `services/category.list_categories` applies, so the tree this
 * builds is a REGROUPING of the server's list rather than a re-sort of it.
 * Ties fell back to input order before, which made two categories at
 * `sortOrder: 0` — every freshly created one — swap places between renders.
 */
function bySortOrderThenName(a: Category, b: Category): number {
  return a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
}

/**
 * Groups a flat category list into a two-level parent → children tree.
 *
 * ORPHAN PROMOTION: a category whose `parentId` points at a row that is NOT in
 * `categories` is returned as a ROOT rather than dropped. This is the rule that
 * makes archiving safe. The picker filters archived categories out of its list,
 * so archiving "Food & Drinks" removes the parent — and without promotion its
 * live "Groceries" and "Dining" would silently vanish from every picker, which
 * is archiving-as-delete for a whole branch and exactly what the
 * archived-category rule forbids. The same promotion happens server-side in
 * `services/category.list_category_tree`, so the two agree.
 *
 * `parentId` is left untouched on the promoted row: restoring the parent
 * restores the nesting with no repair step.
 */
export function buildCategoryTree(categories: Category[]): CategoryTreeNode[] {
  const present = new Set(categories.map((c) => c.id));
  const isRoot = (c: Category) => !c.parentId || !present.has(c.parentId);

  const childrenByParent = new Map<string, Category[]>();
  for (const c of categories) {
    if (isRoot(c)) continue;
    const arr = childrenByParent.get(c.parentId as string) ?? [];
    arr.push(c);
    childrenByParent.set(c.parentId as string, arr);
  }

  return categories
    .filter(isRoot)
    .slice()
    .sort(bySortOrderThenName)
    .map((p) => ({
      ...p,
      children: (childrenByParent.get(p.id) ?? []).slice().sort(bySortOrderThenName),
    }));
}

/**
 * The parents `category` may legally be moved under, mirroring
 * `services/category._validate_parent` so the UI never offers a move the
 * server will reject with a 400.
 *
 * A candidate must be: the same kind; not the category itself; TOP-LEVEL
 * (depth is exactly two); and not archived (archiving means "not offered going
 * forward", so nothing new may be nested under it).
 *
 * Returns an empty list when `category` has children of its own — moving it
 * would push them to depth three, which the server refuses. Callers use that to
 * disable the action with an explanation rather than surfacing a toast after
 * the fact.
 */
export function validParentsFor(category: Category, categories: Category[]): Category[] {
  const hasChildren = categories.some((c) => c.parentId === category.id);
  if (hasChildren) return [];

  return categories
    .filter(
      (c) =>
        c.id !== category.id &&
        c.kind === category.kind &&
        !c.parentId &&
        !c.isArchived,
    )
    .slice()
    .sort(bySortOrderThenName);
}

/**
 * The categories `source` may legally be merged into, mirroring the guards in
 * `services/category.merge_categories`.
 *
 * Beyond "not itself, not archived": the target may not be one of the source's
 * own children (the merge re-parents children onto the target, which would
 * leave it parented to itself), and when the source HAS children the target
 * must be top-level (those children would otherwise land at depth three).
 */
export function validMergeTargets(source: Category, categories: Category[]): Category[] {
  const sourceHasChildren = categories.some((c) => c.parentId === source.id);

  return categories.filter(
    (c) =>
      c.id !== source.id &&
      !c.isArchived &&
      c.kind === source.kind &&
      c.parentId !== source.id &&
      (!sourceHasChildren || !c.parentId),
  );
}

/**
 * Case-insensitive substring filter across a flat category list, in both
 * directions of the tree — the search box has to work on a two-level taxonomy
 * whichever level the user is thinking in:
 *
 *  * UP: a parent is kept when one of its CHILDREN matches, so typing "snacks"
 *    still renders the "Food & Drinks" heading the result sits under rather
 *    than an orphaned child;
 *  * DOWN: a parent's children are kept when the PARENT matches, so typing
 *    "food" offers the whole branch — Groceries, Dining and the rest — instead
 *    of one lone parent row with the subcategories the user was reaching for
 *    filtered away.
 */
export function filterCategoriesByQuery(categories: Category[], query: string): Category[] {
  const q = query.trim().toLowerCase();
  if (!q) return categories;

  const matches = (c: Category) => c.name.toLowerCase().includes(q);
  const matched = categories.filter(matches);
  const matchedParentIds = new Set(
    matched.filter((c) => c.parentId).map((c) => c.parentId as string),
  );
  const matchedRootIds = new Set(matched.filter((c) => !c.parentId).map((c) => c.id));

  return categories.filter(
    (c) =>
      matches(c) ||
      matchedParentIds.has(c.id) ||
      (c.parentId !== null && matchedRootIds.has(c.parentId)),
  );
}

/**
 * Given a sibling list (already sorted by sortOrder), returns the pair of
 * categories whose sortOrder should be swapped to move `id` up/down, or null
 * if the move is out of bounds.
 */
export function findReorderSwap(
  siblings: Category[],
  id: string,
  direction: "up" | "down",
): readonly [Category, Category] | null {
  const idx = siblings.findIndex((c) => c.id === id);
  if (idx === -1) return null;
  const swapIdx = direction === "up" ? idx - 1 : idx + 1;
  if (swapIdx < 0 || swapIdx >= siblings.length) return null;
  return [siblings[idx], siblings[swapIdx]] as const;
}
